import React, { useState } from 'react';
import { Container, Box, Typography, Button, Link } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';

const pageVariants = {
  initial: { opacity: 0, x: -50 },
  enter: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 50 }
};

const AuthPage: React.FC = () => {
  const [view, setView] = useState<'main' | 'login' | 'register'>('main');

  return (
    <Container maxWidth="sm">
      <Box 
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 3,
          py: 4
        }}
      >
        <AnimatePresence mode="wait">
          {view === 'main' && (
            <motion.div
              key="main"
              initial="initial"
              animate="enter"
              exit="exit"
              variants={pageVariants}
              transition={{ duration: 0.3 }}
            >
              {/* Logo */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography 
                  variant="h2" 
                  component="h1" 
                  sx={{ 
                    color: 'primary.main',
                    fontWeight: 'bold',
                    fontSize: { xs: '2.5rem', sm: '3rem', md: '3rem', lg: '3rem', xl: '3rem' },
                  }}
                >
                  Intuitive Fat Loss
                </Typography>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  Lose fat, not your sanity.
                </Typography>
              </Box>

              {/* Auth Buttons */}
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => setView('register')}
                sx={{
                  borderRadius: 8,
                  py: 1.5,
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  backgroundColor: '#00A3FF',
                  '&:hover': {
                    backgroundColor: '#0093E9'
                  },
                  mb: 2
                }}
              >
                Create Account
              </Button>
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={() => setView('login')}
                sx={{
                  borderRadius: 8,
                  py: 1.5,
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  borderColor: '#00A3FF',
                  color: '#00A3FF',
                  '&:hover': {
                    borderColor: '#0093E9',
                    backgroundColor: 'rgba(0, 163, 255, 0.04)'
                  }
                }}
              >
                Sign In
              </Button>

              {/* Support Email */}
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  App issues? Email us: {' '}
                  <Link 
                    href="mailto:support@intuitivefatloss.com"
                    sx={{ 
                      color: 'primary.main',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    support@intuitivefatloss.com
                  </Link>
                </Typography>
              </Box>
            </motion.div>
          )}

          {view === 'login' && (
            <motion.div
              key="login"
              initial="initial"
              animate="enter"
              exit="exit"
              variants={pageVariants}
              transition={{ duration: 0.3 }}
            >
              <LoginForm onBackClick={() => setView('main')} />
            </motion.div>
          )}

          {view === 'register' && (
            <motion.div
              key="register"
              initial="initial"
              animate="enter"
              exit="exit"
              variants={pageVariants}
              transition={{ duration: 0.3 }}
            >
              <RegisterForm onBackClick={() => setView('main')} />
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Container>
  );
};

export default AuthPage;
